import React, {ReactNode, useRef, useState} from 'react';
import {AsideHeader, AsideHeaderItem, MenuItem} from '@gravity-ui/navigation';
import styles from './Layout.module.scss';
import {Avatar, Popup, Text, Button, useOutsideClick, User} from '@gravity-ui/uikit';
import {Bell, CircleQuestion, Gear, Dots9, Magnifier, ArrowRightFromSquare} from '@gravity-ui/icons';
import classNames from 'classnames';
import {useUser, useSession, AppRole} from '../../../entities';
import {LogoutForm} from '../../../features/auth/ui';
import {useNavigate} from 'react-router';

interface LayoutProps {
  children: ReactNode;
  menuItems: MenuItem[];
}

interface ServiceItem {
  title: string;
  path: string;
}

// Список разделов меню «All services» по ролям. Должен совпадать с доступом
// маршрутов (allowedRoles в роутере): представителю — только его кабинет.
const servicesByRole: Record<AppRole, ServiceItem[]> = {
  admin: [
    {title: 'Сборы', path: '/fundraisings/appeals'},
    {title: 'Учетные записи', path: '/accounts/admins'},
    {title: 'Контент', path: '/content/blocks'},
    {title: 'Сообщения', path: '/notifications/notifications-list'},
    {title: 'Переписка', path: '/conversations'},
    {title: 'Администрирование', path: '/administration/consents-documents'},
    {title: 'Проект', path: '/project'},
  ],
  representative: [{title: 'Кабинет', path: '/applications'}],
};

export const Layout = ({children, menuItems}: LayoutProps) => {
  const servicesRef = useRef<HTMLDivElement>(null);
  const servicesPopupRef = useRef<HTMLDivElement>(null);
  const [servicesPopupOpen, setServicesPopupOpen] = useState<boolean>(false);
  const navigate = useNavigate();

  const subHeaderItems: AsideHeaderItem[] = [
    {
      id: 'services',
      title: 'All services',
      icon: Dots9,
      iconSize: 18,
      itemWrapper: (p, makeItem) => {
        return (
          <div className={styles.wrapper} ref={servicesRef} onClick={() => setServicesPopupOpen(true)}>
            {makeItem({
              ...p,
              icon: (
                <div className={styles.icon}>
                  <Dots9 />
                </div>
              ),
            })}
          </div>
        );
      },
    },
    {id: 'search', title: 'Search', icon: Magnifier, iconSize: 18},
  ];

  const [isCompact, setIsCompact] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);
  const [openLogoutModal, setOpenLogoutModal] = useState<boolean>(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const {user} = useUser();
  const {role} = useSession();

  useOutsideClick({
    handler: (e) => {
      if (buttonRef.current?.contains(e.target as Node)) {
        return;
      }
      setOpen(false);
    },
    ref: popupRef as React.RefObject<HTMLElement>,
  });

  useOutsideClick({
    handler: (e) => {
      if (servicesRef.current?.contains(e.target as Node)) {
        return;
      }
      setServicesPopupOpen(false);
    },
    ref: servicesPopupRef as React.RefObject<HTMLElement>,
  });

  const renderAsideFooter = (compact: boolean) => (
    <div className={styles.footerItems}>
      <div className={classNames({[styles.footerItem]: !isCompact})}>
        <Bell cursor={'pointer'} width={18} height={18} />
        {!compact && <Text variant={'body-1'}>Уведомления</Text>}
      </div>
      <div className={classNames({[styles.footerItem]: !isCompact})}>
        <CircleQuestion cursor={'pointer'} width={18} height={18} />
        {!compact && <Text variant={'body-1'}>Помощь</Text>}
      </div>
      <div className={classNames({[styles.footerItem]: !isCompact})}>
        <Gear cursor={'pointer'} width={18} height={18} />
        {!compact && <Text variant={'body-1'}>Settings</Text>}
      </div>
      <div className={classNames({[styles.footerItem]: !isCompact})}>
        <Button
          view="flat"
          ref={buttonRef}
          className={classNames(styles.avatarButton, {[styles.avatarButtonNotCompact]: !compact})}
          onClick={() => setOpen(true)}>
          <Avatar imgUrl={user?.avatarUrl || ''} text={user?.displayName || ''} />
          {!compact && <Text variant={'body-1'}>Учетная запись</Text>}
        </Button>{' '}
      </div>

      <Popup anchorElement={buttonRef.current} open={open} onOpenChange={() => setOpen(false)} placement="right">
        <div ref={popupRef} className={styles.userPopup}>
          <User
            size={'s'}
            avatar={{imgUrl: user?.avatarUrl || '', text: user?.displayName || ''}}
            name={user?.displayName || ''}
          />
          <ArrowRightFromSquare onClick={() => setOpenLogoutModal(true)} />
        </div>
      </Popup>
    </div>
  );

  return (
    <div className={styles.layout}>
      <AsideHeader
        multipleTooltip={true}
        compact={isCompact}
        className={styles.asideHeader}
        renderFooter={() => renderAsideFooter(isCompact)}
        headerDecoration={true}
        menuItems={menuItems}
        renderContent={() => (
          <main>
            {children}
            <Popup
              anchorElement={servicesRef.current}
              open={servicesPopupOpen}
              onOpenChange={setServicesPopupOpen}
              placement="bottom-start">
              <div ref={servicesPopupRef} className={styles.servicesPopup}>
                {servicesByRole[role].map((service) => (
                  <div
                    key={service.path}
                    className={styles.serviceItem}
                    onClick={() => {
                      navigate(service.path);
                      setServicesPopupOpen(false);
                    }}>
                    {service.title}
                  </div>
                ))}
              </div>
            </Popup>
          </main>
        )}
        onChangeCompact={setIsCompact}
        subheaderItems={subHeaderItems}
        logo={{
          iconSrc: '/assets/logo.svg',
          text: 'На старт!',
          href: '/',
        }}
      />
      <LogoutForm isOpen={openLogoutModal} setOpen={setOpenLogoutModal} />
    </div>
  );
};
